import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { connect } from "react-redux";
import { catchError, of, switchMap, tap, map, Subject, takeUntil } from "rxjs";
import PropTypes from "prop-types";
import SourceEntity from "../../models/SourceEntity";
import api from "../../../../services/apiClient";
import rxErrorHandler from "../../../../common/helpers/rxErrorHandler";
import Article from "../../models/Article";
import PublicationFilters from "../PublicationFilters";
import Loader from "../../../Loader";
import { getIsThrottling } from "../../../../state/selectors/Publications.selectors";
import ThrottlingMessage from "../../../atoms/ThrottlingMessage/ThrottlingMessage";
import Modal from "../../../atoms/Modal";
import PublicationListItem from "../PublicationListItem";
import PubmedArticle from "../PubmedArticle";
import Btn from "../../../atoms/Btn";
import { MAX_TOTAL_RESULTS } from "../../constants/displayConf";
import { PUB_DATE_VAL, RELEVANCE_VAL } from "../../constants/sortOptions";

function clean(obj) {
  return Object.entries(obj)
    .filter((x) => x[1] !== null && x[1] !== undefined && x[1] !== "")
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
}

function PublicationSearchResults({
  sourceEntity,
  isThrottling,
  shouldShowAbstract,
  shouldShowTags,
  shouldShowContributions,
  isModal,
  children,
}) {
  const sId = sourceEntity?.id;
  const [selectedTags, setSelectedTags] = useState([]);
  const [timelineTag, setTimelineTag] = useState(null);
  const [publications, setPublications] = useState(null);
  const [showTimeline, setShowTimeline] = useState(false);
  const [selectedArticle, setSelected] = useState({ article: null, tab: null });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState("intersection");
  const [totalResults, setTotalResults] = useState(0);
  const [orderBy, setOrderBy] = useState(sId ? RELEVANCE_VAL : PUB_DATE_VAL);
  const maxHeight = useRef(0);
  const contentRef = useRef(null);

  const filterTagSet = useMemo(
    () =>
      selectedTags
        .concat([timelineTag])
        .filter(Boolean)
        .map((x) => x.typeKey)
        .reduce((a, i) => a.add(i), new Set()),

    [selectedTags, timelineTag]
  );

  useEffect(() => {
    setPage(1);
  }, [pageSize, orderBy, sourceEntity?.id, selectedTags, mode]);

  const adjustContent = useCallback(() => {
    const contentRefEl = contentRef.current;
    const containerEl = isModal
      ? contentRefEl.parentElement.parentElement.parentElement
      : contentRefEl.parentElement.parentElement;

    const parentHeight = containerEl.clientHeight;

    maxHeight.current = Math.max(parentHeight, maxHeight.current);
    contentRefEl.style.minHeight = `${maxHeight.current}px`;
  }, [isModal]);

  useEffect(() => {
    const destroy$ = new Subject();
    const startPage = page - 1;
    const fromOffset = startPage * pageSize;
    const size = pageSize;
    const sort = orderBy ?? undefined;
    const tagFilterMode = mode === "intersection" ? "AND" : "OR";
    const tags = selectedTags.concat([timelineTag]).filter(Boolean);
    const yearTags = tags.filter((x) => x.year).map((x) => x.key);
    const nonYearTags = tags.filter((x) => !x.year).map((x) => x.typeKey);
    const tagFilters = nonYearTags.join("~");
    const tagYearFilters = yearTags.join("~");
    const relevanceType = sourceEntity?.relevanceType;
    const relevanceTypeId = sourceEntity?.relevanceTypeId;
    const filters = { tagFilterMode, tagFilters, tagYearFilters };
    const relevance = { relevanceType, relevanceTypeId };
    const params = clean({ fromOffset, size, sort, ...filters, ...relevance });

    function getTypeId() {
      if (sourceEntity?.searchTypeId) {
        return of(sourceEntity);
      }

      const pubIds = sourceEntity?.pubIds || [];
      const idsStr = pubIds.join("-");
      const existing = (window.lookups || {})[idsStr];

      if (existing) {
        sourceEntity.setLookup(existing);
        return of(sourceEntity);
      }

      return api.postPubLookup(pubIds).pipe(
        map((x) => x.doc_id),
        tap((x) => (window.lookups = { ...window.lookups, [idsStr]: x })),
        map((x) => sourceEntity.setLookup(x))
      );
    }

    of(null)
      .pipe(
        tap(() => setLoading(true)),
        switchMap(() => getTypeId()),
        switchMap((x) => api.searchPubs(x.searchType, x.searchTypeId, params)),
        tap((x) => setTotalResults(Math.min(MAX_TOTAL_RESULTS, x?.total || 0))),
        map((x) => x?.results || []),
        map((x) => x.map((y) => new Article(y))),
        tap((pubs) => setPublications(pubs)),
        catchError(rxErrorHandler(setError, null)),
        tap(() => setLoading(false)),
        takeUntil(destroy$)
      )
      .subscribe();

    return () => destroy$.next();
  }, [
    sourceEntity?.id,
    page,
    orderBy,
    pageSize,
    selectedTags,
    timelineTag,
    mode,
  ]);

  useEffect(() => {
    const el = contentRef.current?.parentElement?.parentElement?.parentElement;
    maxHeight.current = 0;

    if (el) el.scrollTop = 0;
  }, [selectedArticle.article]);

  if (isModal && selectedArticle.article) {
    return (
      <div ref={contentRef} style={{ minHeight: `${maxHeight}px` }}>
        <div className="tw-absolute tw-bg-white tw-top-0 tw-z-10 tw-left-3 tw-right-12 tw-p-4 tw-px-6 tw-border-b tw-border-blueGray-200">
          <Btn
            className="tw-text-blue-600 tw-font-bold tw-flex tw-items-center"
            onSubmit={() => setSelected({ article: null, tab: null })}
          >
            <i className="fa fa-arrow-circle-left" />
            <div className="tw-w-2" />
            Back ({totalResults} publications)
          </Btn>
        </div>
        <div onClick={adjustContent} className="tw-min-h-96 tw-py-8 tw-px-4">
          <div className="tw-h-8" />
          <PubmedArticle
            sourceEntity={sourceEntity}
            article={selectedArticle.article}
            tab={selectedArticle.tab}
          />
        </div>
      </div>
    );
  }

  if (isThrottling) {
    return (
      <div className="tw-py-10">
        <ThrottlingMessage />
      </div>
    );
  }

  return (
    <div
      onClick={() => (isModal ? adjustContent() : null)}
      style={{ minHeight: `${maxHeight}px` }}
      ref={isModal ? contentRef : null}
    >
      <div className={`tw-min-h-56 ${isModal ? "tw-pt-4" : ""}`}>
        {error ? (
          <div>Something went wrong...</div>
        ) : (
          <>
            <PublicationFilters
              setShowTimeline={setShowTimeline}
              showTimeline={showTimeline}
              selectedTags={selectedTags}
              setSelectedTags={setSelectedTags}
              timelineTag={timelineTag}
              setTimelineTag={setTimelineTag}
              type={sourceEntity.searchType}
              typeId={sourceEntity.searchTypeId}
              totalResults={totalResults}
              orderBy={orderBy}
              setOrderBy={setOrderBy}
              pageSize={pageSize}
              filterMode={mode}
              setFilterMode={setMode}
              setPageSize={setPageSize}
              page={page}
              setPage={setPage}
              sourceEntity={sourceEntity}
            />

            <div className="tw-border-t tw-border-blueGray-300 tw-relative">
              {children}
              <div className={isLoading ? "tw-opacity-30" : ""}>
                {(publications || []).map((x) => (
                  <PublicationListItem
                    key={x.id}
                    sourceEntity={sourceEntity}
                    onSelect={setSelected}
                    article={x}
                    shouldShowAbstract={shouldShowAbstract}
                    shouldShowTags={shouldShowTags}
                    shouldShowContributions={shouldShowContributions}
                    filterTagSet={filterTagSet}
                  />
                ))}
              </div>

              {isLoading ? (
                <div className="tw-absolute tw-inset-0">
                  <Loader />
                </div>
              ) : null}
              <Modal
                isOpen={Boolean(selectedArticle.article)}
                onRequestClose={() => setSelected({ article: null, tab: null })}
              >
                <div
                  className="tw-w-90screen tw-max-w-screen-xl"
                  ref={contentRef}
                >
                  <div
                    onClick={adjustContent}
                    className="tw-min-h-96 tw-py-8 tw-px-10"
                  >
                    <PubmedArticle
                      sourceEntity={sourceEntity}
                      article={selectedArticle.article}
                      initTab={selectedArticle.tab}
                    />
                  </div>
                </div>
              </Modal>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

PublicationSearchResults.propTypes = {
  sourceEntity: PropTypes.instanceOf(SourceEntity),
  isThrottling: PropTypes.bool,
  shouldShowAbstract: PropTypes.bool,
  shouldShowTags: PropTypes.bool,
  shouldShowContributions: PropTypes.bool,
  isModal: PropTypes.bool,
  children: PropTypes.node,
};

PublicationSearchResults.defaultProps = {
  shouldShowAbstract: false,
  shouldShowTags: false,
  shouldShowContributions: false,
  sourceEntity: null,
  isThrottling: false,
  isModal: false,
  children: null,
};

const mapStateToProps = (state) => ({
  isThrottling: getIsThrottling(state),
});

export default connect(mapStateToProps)(React.memo(PublicationSearchResults));
